import React, { useEffect, useState } from "react";
import { Button, Form, Input, message, Modal, Space } from "antd";
import "./add_line_pr.css";
import BrowserSparepartsPR, { DataType as SparepartDataType } from "./browser_spareparts_pr";
import axios from "axios";
import { CloseCircleOutlined, LoadingOutlined } from "@ant-design/icons";

interface AddLinePrProps {
    visible?: boolean;
    onClose?: () => void;
    addItem?: (itemData: any) => void;
    editItem?: any;
    onEditItem?: (itemData: any) => void;
    disabled?: boolean;
    dealerRepCode?: string;
}

const AddLinePr: React.FC<AddLinePrProps> = ({ visible, onClose, addItem, editItem, onEditItem, disabled, dealerRepCode }) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [form] = Form.useForm();
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(false);
    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const [isSparepartsModalVisible, setIsSparepartsModalVisible] = useState(false);
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    useEffect(() => {
        if (editItem) {
            setIsEditMode(true);
            form.setFieldsValue({
                ...editItem,
                qty: editItem.quantity
            });
        } else {
            setIsEditMode(false);
            form.resetFields();
        }
    }, [editItem, visible]);

    const showSparepartsModal = () => {
        setIsSparepartsModalVisible(true);
    };

    const handleCloseSparepartsModal = () => {
        setIsSparepartsModalVisible(false);
    };

    const handleSparepartSelect = (selectedItem: SparepartDataType) => {
        form.setFieldsValue({
            item_code: selectedItem.item_code,
            item_name: selectedItem.item_name,
            uom: selectedItem.uom,
            uom_rate: 1,
        });
        setSearchError(false);
        setIsSparepartsModalVisible(false);
    };

    const handleItemCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchError(false);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (!value) {
            form.setFieldsValue({ item_name: undefined, uom: undefined, uom_rate: undefined });
            return;
        }
        if (isEditMode) return;
        searchTimeoutRef.current = setTimeout(async () => {
            setIsSearching(true);
            const startTime = Date.now();
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts`, {
                    params: {
                        item_code: value,
                        warehouse_group: dealerRepCode
                    }
                });
                const elapsed = Date.now() - startTime;
                if (elapsed < 800) await new Promise(resolve => setTimeout(resolve, 800 - elapsed));
                if (response.data?.data?.length > 0 && response.data.data[0].item_code?.toLowerCase() === value.toLowerCase()) {
                    const item = response.data.data[0];
                    form.setFieldsValue({ item_name: item.item_name, uom: item.uom, uom_rate: item.uom_rate || 1 });
                    setSearchError(false);
                } else {
                    setSearchError(true);
                }
            } catch (err) {
                setSearchError(true);
            } finally {
                setIsSearching(false);
            }
        }, 1000);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!values.item_code) {
                message.error("Item Code tidak boleh kosong. Silakan cari atau isi manual.");
                return;
            }
            const transformedData = {
                ...values,
                quantity: values.qty,
                item_code: values.item_code
            };
            if (isEditMode) {
                onEditItem?.(transformedData);
            } else {
                addItem?.(transformedData);
            }
            form.resetFields();
            onClose?.();
        } catch (error) {
            console.error("Validasi gagal:", error);
            if (error instanceof Error) {
                message.error(`Validasi gagal: ${error.message}`);
            } else if (typeof error === 'object' && error !== null && 'errorFields' in error) {
                const errorFields = (error as any).errorFields;
                if (errorFields && errorFields.length > 0) {
                    const firstError = errorFields[0].errors[0];
                    message.error(`Validasi gagal: ${firstError}`);
                } else {
                    message.error("Validasi gagal. Periksa kembali input Anda.");
                }
            } else {
                message.error("Validasi gagal. Periksa kembali input Anda.");
            }
        }
    };

    const handleMainModalCancel = () => {
        form.resetFields();
        onClose?.();
    };

    return (

        <div id="iau9" style={transformStyle}>
            <div className="gjs-grid-row" id="ia7l">
                <div className="gjs-grid-column" id="iggl">
                    <Modal
                        title={isEditMode ? "Edit Item" : "Add Item"}
                        open={visible}
                        onCancel={handleMainModalCancel}
                        footer={null}
                        width={400}
                        styles={{
                            body: {
                                transform: "scale(0.80)",
                                transformOrigin: "top left",
                                width: "125%",
                            }
                        }}
                        destroyOnClose={false}
                    >
                        <Form
                            layout="horizontal"
                            form={form}
                            onFinish={handleSubmit}
                            style={{ maxWidth: 500 }}
                            labelCol={{ style: { width: '150px', textAlign: "left" } }}
                            wrapperCol={{ style: { width: '290px' } }}
                            className="custom-info-purchase-request"
                            preserve={false}
                        >
                            <Form.Item
                                label="Item Code"
                                validateStatus={searchError ? 'error' : ''}
                                help={searchError ? 'Item Code tidak ditemukan di master' : ''}
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <Form.Item name="item_code" noStyle>
                                        <Input
                                            name="item_code"
                                            placeholder=""
                                            style={{ width: "100%" }}
                                            readOnly={isEditMode}
                                            onChange={handleItemCodeChange}
                                            allowClear
                                            suffix={
                                                isSearching ? <LoadingOutlined spin style={{ color: '#1890ff' }} /> :
                                                    searchError ? <CloseCircleOutlined style={{ color: 'red' }} /> : null
                                            }
                                        />
                                    </Form.Item>
                                    {!isEditMode && (
                                        <Button
                                            type="primary"
                                            id="browser_spareparts_pr"
                                            onClick={showSparepartsModal}
                                            disabled={disabled}
                                            icon={
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                    style={{
                                                        width: "40px",
                                                        height: "20px",
                                                    }}
                                                >
                                                    <path d="M11.625 16.5a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75Z" />
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875Zm6 16.5c.66 0 1.277-.19 1.797-.518l1.048 1.048a.75.75 0 0 0 1.06-1.06l-1.047-1.048A3.375 3.375 0 1 0 11.625 18Z"
                                                        clipRule="evenodd"
                                                    />
                                                    <path d="M14.25 5.25a5.23 5.23 0 0 0-1.279-3.434 9.768 9.768 0 0 1 6.963 6.963A5.23 5.23 0 0 0 16.5 7.5h-1.875a.375.375 0 0 1-.375-.375V5.25Z" />
                                                </svg>
                                            }
                                        />
                                    )}
                                </div>
                            </Form.Item>
                            <Form.Item
                                label="Item Name"
                                name="item_name"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" readOnly />
                            </Form.Item>

                            <Form.Item
                                label="UoM"
                                name="uom"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" readOnly />
                            </Form.Item>
                            <Form.Item
                                label="UoM Rate"
                                name="uom_rate"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" readOnly />
                            </Form.Item>
                            <Form.Item
                                label="Qty"
                                name="qty"
                                style={{
                                    marginBottom: "5px",
                                }}
                                rules={[{ required: true, message: 'Required !' }]}
                            >
                                <Input placeholder="" />
                            </Form.Item>
                            <Form.Item
                                label="Remark"
                                name="remark_table_pr"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input.TextArea rows={4} placeholder="" />
                            </Form.Item>
                            <Space style={{ paddingTop: "20px" }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    style={{
                                        height: "40px",
                                        width: "120px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            style={{ width: "22px", height: "22px" }}
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    }
                                >
                                    OK
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleMainModalCancel}
                                    style={{
                                        height: "40px",
                                        width: "120px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "red",
                                        color: "white",
                                        border: "none",
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </Form>
                    </Modal>
                    <Modal
                        title="Browse Spare Parts"
                        open={isSparepartsModalVisible}
                        onCancel={handleCloseSparepartsModal}
                        footer={null}
                        width="fit-content"
                        destroyOnClose
                    >
                        <BrowserSparepartsPR onSelect={handleSparepartSelect} dealerRepCode={dealerRepCode} />
                    </Modal>
                </div>
            </div>
        </div>
    );
};

export default AddLinePr;